// The Construction tab's geometry, pure: planes (fitted to points, drawn, sized and tilted), one-off shapes
// moved and reshaped, a type's box moved or resized from one of its instances, and what is under the cursor.
// Shapes and planes are kept in a frame's file coordinates (src/fit/doc.ts) and drawn at M = T · E(frame); a
// move W made in the world is M⁻¹ · W · M there.
import { type Rigid, apply, compose, identity, inverse, rot } from '../align.ts';
import { type V3, add, cross, dot, norm, rotate, scale, sub } from '../math.ts';
import { type PlaneDoc, type ShapeDoc, isPolePiece, roundPlane } from '../fit/doc.ts';
import { type TypeBox, GROUP_NAME } from './log.ts';
import type { OpBody } from './session.ts';
import { type ObjInfo, boxInOverlay } from './overlay.ts';
import { eigSym3 } from './select.ts';

/** A move W made in the world, as it is in a frame drawn at M: M⁻¹ · W · M. */
export const inFrame = (M: Rigid, W: Rigid): Rigid => compose(inverse(M), compose(W, M));

const v3 = (a: number[]): V3 => [a[0], a[1], a[2]];

/** A shape moved rigidly by A (in its own coordinates): poles' centrelines, surfaces' planes; sizes kept. */
export function transformShape(d: ShapeDoc, A: Rigid): ShapeDoc {
  return {
    ...d,
    pieces: d.pieces.map(q => {
      if (isPolePiece(q)) {
        const c: number[] = [];
        for (let i = 0; i < q.c.length; i += 3) c.push(...apply(A, [q.c[i], q.c[i + 1], q.c[i + 2]]));
        return { ...q, c };
      }
      return { ...q, o: apply(A, v3(q.o)), u: rot(A, v3(q.u)), v: rot(A, v3(q.v)) };
    }),
  };
}

/** A plane moved rigidly by A (in its own coordinates). */
export const transformPlane = (d: PlaneDoc, A: Rigid): PlaneDoc => ({ ...d, o: apply(A, v3(d.o)), u: rot(A, v3(d.u)), v: rot(A, v3(d.v)) });

/** Every pole piece's radius scaled so the largest is r (m), its taper kept; a pole with none, r throughout. */
export function setPoleRadius(d: ShapeDoc, r: number): ShapeDoc {
  const top = Math.max(0, ...d.pieces.flatMap(q => (isPolePiece(q) ? q.r : [])));
  return { ...d, pieces: d.pieces.map(q => (isPolePiece(q) ? { ...q, r: q.r.map(x => (top > 0 ? (x * r) / top : r)) } : q)) };
}

/** Every surface piece's thickness set to t (m). */
export const setSurfaceThickness = (d: ShapeDoc, t: number): ShapeDoc =>
  ({ ...d, pieces: d.pieces.map(q => (isPolePiece(q) ? q : { ...q, t })) });

/** Pole piece k's control point i put at p (its own coordinates). */
export function movePoleCtrl(d: ShapeDoc, k: number, i: number, p: V3): ShapeDoc {
  return {
    ...d,
    pieces: d.pieces.map((q, j) => {
      if (j !== k || !isPolePiece(q)) return q;
      const c = q.c.slice();
      c.splice(3 * i, 3, p[0], p[1], p[2]);
      return { ...q, c };
    }),
  };
}

/** Each pole piece's control points (its own coordinates), placed by M. */
export function poleCtrls(d: ShapeDoc, M: Rigid): { k: number; i: number; p: V3 }[] {
  const out: { k: number; i: number; p: V3 }[] = [];
  d.pieces.forEach((q, k) => { if (isPolePiece(q)) for (let i = 0; 3 * i < q.c.length; i++) out.push({ k, i, p: apply(M, [q.c[3 * i], q.c[3 * i + 1], q.c[3 * i + 2]]) }); });
  return out;
}

/** Where a shape is: the middle of its points' extent (its own coordinates). */
export function shapeCentre(d: ShapeDoc): V3 {
  const lo: V3 = [Infinity, Infinity, Infinity], hi: V3 = [-Infinity, -Infinity, -Infinity];
  const take = (p: V3) => { for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], p[k]); hi[k] = Math.max(hi[k], p[k]); } };
  for (const q of d.pieces) {
    if (isPolePiece(q)) for (let i = 0; i < q.c.length; i += 3) take([q.c[i], q.c[i + 1], q.c[i + 2]]);
    else for (const a of [q.ab[0], q.ab[1]]) for (const b of [q.ab[2], q.ab[3]]) take(add(v3(q.o), add(scale(v3(q.u), a), scale(v3(q.v), b))));
  }
  return lo[0] <= hi[0] ? scale(add(lo, hi), 0.5) : [0, 0, 0];
}

// ── planes ───────────────────────────────────────────────────────────────

/** A plane's normal (u × v). */
export const planeNormal = (d: PlaneDoc): V3 => norm(cross(v3(d.u), v3(d.v)));
/** The middle of a plane's rectangle (its own coordinates). */
export const planeCentre = (d: PlaneDoc): V3 =>
  add(v3(d.o), add(scale(v3(d.u), (d.ab[0] + d.ab[1]) / 2), scale(v3(d.v), (d.ab[2] + d.ab[3]) / 2)));
/** Its width along u and height along v (m). */
export const planeSize = (d: PlaneDoc): [number, number] => [d.ab[1] - d.ab[0], d.ab[3] - d.ab[2]];

/** The rectangle's corners, placed by M: (a0,b0) (a1,b0) (a1,b1) (a0,b1). */
export function planeCorners(d: PlaneDoc, M: Rigid = identity()): V3[] {
  const [a0, a1, b0, b1] = d.ab, at = (a: number, b: number) => apply(M, add(v3(d.o), add(scale(v3(d.u), a), scale(v3(d.v), b))));
  return [at(a0, b0), at(a1, b0), at(a1, b1), at(a0, b1)];
}

/** The plane's wireframe placed by M: its outline, both diagonals and a normal tick from its centre (a tenth of its size, 5 cm at least). */
export function planeLines(d: PlaneDoc, M: Rigid = identity()): Float32Array {
  const c = planeCorners(d, M), out: number[] = [];
  for (let i = 0; i < 4; i++) out.push(...c[i], ...c[(i + 1) % 4]);
  out.push(...c[0], ...c[2], ...c[1], ...c[3]);
  const m = apply(M, planeCentre(d)), n = rot(M, planeNormal(d)), L = Math.max(0.05, 0.1 * Math.max(...planeSize(d)));
  out.push(...m, ...add(m, scale(n, L)));
  return new Float32Array(out);
}

/** The plane made w by h (m) about its centre (its axes kept). */
export function setPlaneSize(d: PlaneDoc, w: number, h: number): PlaneDoc {
  const ca = (d.ab[0] + d.ab[1]) / 2, cb = (d.ab[2] + d.ab[3]) / 2;
  return { ...d, ab: [ca - w / 2, ca + w / 2, cb - h / 2, cb + h / 2] };
}

/** The plane turned by deg about its own u (k 0) or v (k 1) axis through its centre: tilted, where the gizmo only turns about the vertical. */
export function tiltPlane(d: PlaneDoc, k: 0 | 1, deg: number): PlaneDoc {
  const a = (deg * Math.PI) / 180, axis = norm(v3(k ? d.v : d.u)), c = planeCentre(d);
  const turn = (p: V3) => rotate(p, axis, a), o = add(c, turn(sub(v3(d.o), c)));
  return { ...d, o, u: norm(turn(v3(d.u))), v: norm(turn(v3(d.v))) };
}

/** The plane stood upright (normal level) or laid flat (normal up), whichever it is nearer, about its centre. */
export function squarePlane(d: PlaneDoc): PlaneDoc {
  const n = planeNormal(d), c = planeCentre(d);
  let u: V3, v: V3;
  if (Math.abs(n[2]) > Math.SQRT1_2) {   // nearer flat: u level along its own heading, v across
    const h: V3 = Math.hypot(d.u[0], d.u[1]) > 1e-6 ? norm([d.u[0], d.u[1], 0]) : [1, 0, 0];
    u = h; v = n[2] > 0 ? cross([0, 0, 1], u) : cross(u, [0, 0, 1]);
  } else {   // nearer upright: u level along the wall, v up
    const nh = norm([n[0], n[1], 0]);
    u = cross([0, 0, 1], nh); v = [0, 0, 1];
    if (dot(u, v3(d.u)) < 0) u = scale(u, -1);
    if (dot(cross(u, v), n) < 0) u = scale(u, -1);
  }
  // the same rectangle about the same centre, in the new axes
  const [w, h] = planeSize(d);
  return { ...d, o: c, u, v, ab: [-w / 2, w / 2, -h / 2, h / 2] };
}

/**
 * A plane through points (packed xyz): by PCA (the least spread axis its normal, the most its u), spanning the
 * lo to 1 - lo quantiles along u and v so a few strays don't stretch it; o the centroid. null: fewer than 3
 * points, or all on a line.
 */
export function fitPlaneDoc(xyz: Float32Array, ref: string, lo = 0.02): PlaneDoc | null {
  const n = xyz.length / 3;
  if (n < 3) return null;
  let mx = 0, my = 0, mz = 0;
  for (let i = 0; i < n; i++) { mx += xyz[3 * i]; my += xyz[3 * i + 1]; mz += xyz[3 * i + 2]; }
  mx /= n; my /= n; mz /= n;
  const C = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let i = 0; i < n; i++) {
    const d = [xyz[3 * i] - mx, xyz[3 * i + 1] - my, xyz[3 * i + 2] - mz];
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) C[3 * r + c] += d[r] * d[c];
  }
  const { vals, vecs } = eigSym3(C), ix = [0, 1, 2].sort((a, b) => vals[a] - vals[b]), col = (j: number): V3 => norm([vecs[j], vecs[3 + j], vecs[6 + j]]);
  if (!(vals[ix[1]] > 1e-12 * (vals[ix[2]] || 1))) return null;
  const n0 = col(ix[0]), nrm = n0[2] < 0 ? scale(n0, -1) : n0, u0 = col(ix[2]);   // a floor's normal up
  // u level when the plane is a wall (or the long way when it is a floor or a slope), v = n × u
  let u = Math.abs(nrm[2]) < Math.SQRT1_2 ? norm(cross([0, 0, 1], nrm)) : u0;
  if (Math.abs(nrm[2]) < Math.SQRT1_2 && dot(u, u0) < 0) u = scale(u, -1);
  const v = norm(cross(nrm, u));
  const A = new Float32Array(n), B = new Float32Array(n), o: V3 = [mx, my, mz];
  for (let i = 0; i < n; i++) { const d: V3 = [xyz[3 * i] - mx, xyz[3 * i + 1] - my, xyz[3 * i + 2] - mz]; A[i] = dot(d, u); B[i] = dot(d, v); }
  A.sort(); B.sort();
  const m = Math.min(n - 1, Math.floor(lo * n)), span = (q: Float32Array): [number, number] => {
    const a = q[m], b = q[n - 1 - m];
    return b - a > 1e-4 ? [a, b] : [a - 0.05, b + 0.05];
  };
  const [a0, a1] = span(A), [b0, b1] = span(B);
  return { ref, o, u, v, ab: [a0, a1, b0, b1], n };
}

// ── type boxes, edited from one instance ───────────────────────────────

/**
 * The type's box after a move W made in the world to it as drawn on instance name (at F_name⁻¹ · F_ref · B):
 * B' = F_ref⁻¹ · F_name · W · F_name⁻¹ · F_ref · B, so every instance shows it moved alike on itself.
 */
export function boxMovedOn(box: TypeBox, info: ObjInfo, name: string, W: Rigid): TypeBox {
  const Fi = info.get(name)?.F ?? identity(), Fr = info.get(box.ref)?.F ?? identity();
  const B = compose(inverse(Fr), compose(Fi, compose(W, compose(inverse(Fi), boxInOverlay(box, info)))));
  const r = (x: number) => Math.round(x * 1e6) / 1e6;
  return { ref: box.ref, B: { R: B.R.map(r), t: B.t.map(r) as V3 }, s: box.s };
}

/** Where the box sits on instance name, in the world (box frame -> world). */
export const boxOn = (box: TypeBox, info: ObjInfo, name: string): Rigid =>
  compose(inverse(info.get(name)?.F ?? identity()), boxInOverlay(box, info));

/** The box with new edge lengths (m, about its centre). */
export const boxSized = (box: TypeBox, s: V3): TypeBox => ({ ...box, s: s.map(x => Math.round(x * 1e4) / 1e4) as V3 });

// ── under the cursor ─────────────────────────────────────────────────────

/**
 * How near (px) the cursor (x, y) comes to a line set's segments (pairs of points, as Viewer.setLines takes)
 * drawn on the screen by toScreen (null: behind the camera). Infinity: none on the screen.
 */
export function segDist(lines: Float32Array, toScreen: (p: V3) => [number, number] | null, x: number, y: number): number {
  let best = Infinity;
  for (let i = 0; i + 5 < lines.length; i += 6) {
    const a = toScreen([lines[i], lines[i + 1], lines[i + 2]]), b = toScreen([lines[i + 3], lines[i + 4], lines[i + 5]]);
    if (!a || !b) continue;
    const dx = b[0] - a[0], dy = b[1] - a[1], L = dx * dx + dy * dy, t = L ? Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / L)) : 0;
    best = Math.min(best, Math.hypot(a[0] + t * dx - x, a[1] + t * dy - y));
  }
  return best;
}

/**
 * The edits that fit the plane `name` (blank: the next free "plane k") to the selection sel (in its frame's file
 * coordinates, as ConsHost.selection gives it), given the planes there are: plane.set in the selection's scan (and
 * plane.drop in the scan the plane was in, if another); or why not, as a message.
 */
export function planeFit(planes: Map<string, { scan: string }>, name: string, sel: { scan: string; frame: number; xyz: Float32Array; n: number } | null) {
  if (!name) { let k = 1; while (planes.has(`plane ${k}`)) k++; name = `plane ${k}`; }
  if (!GROUP_NAME.test(name)) return 'name the plane: letters, digits, space . : + -';
  if (!sel || sel.n < 3) return 'select the points of the plane first (Lasso, top right)';
  const d = fitPlaneDoc(sel.xyz, `f:${sel.frame}`);
  if (!d) return 'those points lie on a line: no plane through them';
  d.n = sel.n;
  const bodies = new Map<string, OpBody[]>(), old = planes.get(name);
  if (old && old.scan !== sel.scan) bodies.set(old.scan, [{ op: 'plane.drop', name } as OpBody]);
  bodies.set(sel.scan, [{ op: 'plane.set', name, plane: roundPlane(d) } as OpBody]);
  const [w, h] = planeSize(d);
  return { name, scan: sel.scan, again: !!old, bodies, plane: d, said: `plane "${name}": ${w.toFixed(2)} × ${h.toFixed(2)} m, from ${sel.n.toLocaleString()} points` };
}
